import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, siteName, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navLinkClass = isScrolled
    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
    : 'text-white/90 hover:text-white hover:bg-white/15';

  const mobileMenuBg = isScrolled
    ? 'bg-background-50 border-background-200/60'
    : 'bg-black/40 border-white/20';
  const mobileTextClass = isScrolled
    ? 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
    : 'text-white/90 hover:text-white hover:bg-white/10';
  const mobileSubTextClass = isScrolled ? 'text-foreground-500' : 'text-white/60';
  const mobileItemTextClass = isScrolled
    ? 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
    : 'text-white/85 hover:text-white hover:bg-white/10';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-black/25 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-semibold whitespace-nowrap transition-colors ${
              isScrolled ? 'text-foreground-900' : 'text-white'
            }`}
          >
            {siteName}
          </Link>

          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${navLinkClass}`}
            >
              <i className="ri-home-line"></i>
              <span>Home</span>
            </Link>

            <Link
              to="/basetao-shopping-guide"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${navLinkClass}`}
            >
              <i className="ri-guide-line"></i>
              <span>Shopping Guide</span>
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${navLinkClass}`}
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 shadow-lg overflow-hidden animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-80 overflow-y-auto py-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-accent-50 transition-colors cursor-pointer"
                      >
                        <div className="w-7 h-7 rounded-md bg-background-100 overflow-hidden flex-shrink-0">
                          <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium bg-secondary-500 text-background-50 rounded-md hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Basetao Spreadsheet
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors ${
              isScrolled ? 'text-foreground-900' : 'text-white'
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className={`md:hidden border-t py-4 animate-[fadeIn_0.2s_ease-out] ${mobileMenuBg}`}>
            <div className="flex flex-col gap-1 mb-4">
              <Link
                to="/"
                className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md transition-colors cursor-pointer ${mobileTextClass}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-home-line"></i> Home
              </Link>
              <Link
                to="/basetao-shopping-guide"
                className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md transition-colors cursor-pointer ${mobileTextClass}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-guide-line"></i> Shopping Guide
              </Link>
            </div>

            <div className="mb-4">
              <p className={`text-xs px-3 mb-2 uppercase tracking-wider font-medium ${mobileSubTextClass}`}>Product Categories</p>
              <div className="grid grid-cols-2 gap-1 px-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className={`flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors cursor-pointer ${mobileItemTextClass}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="w-6 h-6 rounded bg-white/20 overflow-hidden flex-shrink-0">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <span className="text-xs truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className={`flex flex-col gap-2 pt-3 border-t px-2 ${isScrolled ? 'border-background-200/60' : 'border-white/20'}`}>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-2.5 text-sm font-medium bg-secondary-500 text-background-50 rounded-md text-center hover:bg-secondary-600 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Basetao Spreadsheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-md text-center hover:bg-primary-600 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}